import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Avatar } from '@/components/avatar'
import CloseButton from '@/components/close-button'
import { PhoneLarge, EmailLarge, DisabledEmailLarge, DisabledPhoneLarge } from '@/components/icons'
import { createModal, useModalize } from '@/components/modalize'
import VerifiedBadge from '@/components/verified-badge'
import { breakpoints, colors, fontWeights } from '@/styles'
import { getImageUrl } from '@/utils/image'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 90vh;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: 50vh;
  }
`

const Header = styled.div`
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 25px 0;
  flex-shrink: 0;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: flex-start;
    height: 50px;
    padding: 5px 20px 0;
  }
`

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;

  // use after for bottom padding due to Firefox bug:
  // https://stackoverflow.com/questions/29986977
  :after {
    content: '';
    display: block;
    padding-bottom: 24px;
    width: 100%;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0 16px;
  }
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 760px;
  width: 100%;
`

const Title = styled.h1`
  font-size: 16px;
  line-height: 27px;
  font-weight: ${fontWeights.bold};
`

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  flex-direction: column;
  width: 100%;
`

const Cards = styled.div`
  border-radius: 10px;
  width: 100%;
  flex: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0 auto;
    grid-row: unset;
  }
`

const MemberWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 18px 0;
  padding-left: 18px;
  position: relative;

  :not(:last-child) {
    padding-bottom: 30px;
  }
`

const MemberInfoWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 20px;
`

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 190px;
`

const NameText = styled.p`
  font-size: 20px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 22px;
  letter-spacing: -0.2px;
  margin: 0;
  max-width: 150px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const PrimaryText = styled.p`
  font-size: 14px;
  color: ${colors.grey32};
  line-height: 17px;
  margin: 0;
  margin-top: 6px;
`

const IconsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 104px;
`

const Member = ({ userData }) => {
  return (
    <MemberWrapper>
      <Avatar
        location={getImageUrl(userData.avatar?.aws_s3_key, {
          width: 80,
          height: 80
        })}
        width={80}
        height={80}
        shadow
      />

      <MemberInfoWrapper>
        <NameWrapper>
          <NameText>{userData.name}</NameText>
          <VerifiedBadge verified={userData.isVerified} type="id" width={20} height={20} left={4} />
        </NameWrapper>
        {userData.companyName && <PrimaryText>{userData.companyName}</PrimaryText>}
      </MemberInfoWrapper>
      <IconsWrapper>
        {!!userData.email && !userData.isLocked ? (
          <a href={`mailto:${userData.email}`}>
            <EmailLarge />
          </a>
        ) : (
          <DisabledEmailLarge />
        )}
        {!!userData.phoneNumber && !userData.isLocked ? (
          <a href={`tel:${userData.phoneNumber}`}>
            <PhoneLarge />
          </a>
        ) : (
          <DisabledPhoneLarge />
        )}
      </IconsWrapper>
    </MemberWrapper>
  )
}

const ProfilePictureModal = ({ userData }) => {
  const { t } = useTranslation()
  const { close } = useModalize()

  return (
    <Wrapper>
      <Header>
        <Title>{t('b.chat.view.profile.title', 'Profile')}</Title>
        <CloseButton onClick={close} />
      </Header>
      <ContentWrapper>
        <ContentInner>
          <CardsWrapper>
            <Cards>
              <Member key={userData.id} userData={userData} isLocked={userData.isLocked} />
            </Cards>
          </CardsWrapper>
        </ContentInner>
      </ContentWrapper>
    </Wrapper>
  )
}

export const show = createModal(ProfilePictureModal, {
  width: 'auto',
  maxWidth: '482px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  mobileAlign: 'flex-end',
  tabletFullWidth: true,
  mobileBorderRadius: '16px 16px 0 0'
})
